import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertCircleIcon } from 'lucide-react';
import { log } from '../core/log';
import { clearLocalData } from '../core/storage';
import { isChunkLoadError } from './lazyWithRetry';
import { btnPrimary, btnSecondary } from './styles';
type Props = {
  children: ReactNode;
  title?: string;
  body?: string;
  /** Volledig scherm (voor de buitenste foutgrens), anders een blok binnen de pagina. */
  fullScreen?: boolean;
  /** De veilige plek om naartoe te gaan. Shop: "/", beheer: "/be". */
  homeHref?: string;
  homeLabel?: string;
  /**
   * Biedt "Lokale gegevens herstellen" aan zodra opnieuw proberen niet helpt. Alleen voor de
   * buitenste foutgrenzen: daar kan beschadigde opgeslagen data de hele app laten vastlopen.
   */
  offerReset?: boolean;
  /** Toon de foutmelding ingeklapt onder "Technische details". Alleen in het beheer, niet voor klanten. */
  showDetails?: boolean;
};
type State = {
  error: unknown;
  retries: number;
  confirmReset: boolean;
};

/** Na zoveel keer "Opnieuw proberen" met dezelfde fout is het geen hapering meer maar een structurele fout. */
const MAX_RETRIES = 1;

/**
 * Vangnet per pagina: gaat er iets mis bij het tonen, dan ziet de bezoeker een
 * rustige melding in plaats van een leeg vlak — en blijven menu en header werken.
 * Geef een `key` mee (het pad), dan herstelt doorklikken het vanzelf.
 *
 * Drie lagen in de app: rond alles (App.tsx), rond het beheer, en per pagina (shop, Mijn
 * omgeving, elk beheerscherm). Een fout komt zo altijd terecht bij de kleinste grens eromheen.
 *
 * Vier soorten fouten, vier uitwegen:
 * - **Chunk laadt niet** (nieuwe versie of offline): opnieuw tekenen helpt niet, want React.lazy
 *   onthoudt de mislukte import. De knop vernieuwt de pagina.
 * - **Eenmalige hapering:** "Opnieuw proberen" tekent het onderdeel opnieuw.
 * - **Structurele fout** (bijv. kapotte data): na één mislukte poging verdwijnt "Opnieuw proberen",
 *   zodat niemand blijft klikken in een lus die nooit lukt. Dan blijft altijd een uitweg die niet
 *   van de kapotte pagina afhangt: een gewone link naar een veilige plek (volledig herladen, dus
 *   ook buiten de router om) en de pagina vernieuwen.
 * - **Beschadigde opgeslagen data** (buitenste grens, `offerReset`): wist alleen de sleutels van
 *   deze app in de browser, na een bevestiging, en herlaadt. Account en bestellingen staan op de
 *   server en blijven.
 *
 * Er ontstaat nooit vanzelf een lus: opnieuw tekenen gebeurt alleen na een klik.
 */
export class PageErrorBoundary extends Component<Props, State> {
  state: State = {
    error: null,
    retries: 0,
    confirmReset: false
  };
  static getDerivedStateFromError(error: unknown): Partial<State> {
    return {
      error: error ?? new Error('Onbekende fout')
    };
  }
  componentDidCatch(error: unknown, info: ErrorInfo) {
    log.error('pagina', {
      error,
      retries: this.state.retries,
      path: window.location.pathname
    }, info.componentStack);
  }
  private retry = () => this.setState(s => ({
    error: null,
    retries: s.retries + 1,
    confirmReset: false
  }));
  private reset = () => {
    if (!this.state.confirmReset) {
      this.setState({
        confirmReset: true
      });
      return;
    }
    const removed = clearLocalData();
    log.warn('pagina', 'Lokale gegevens hersteld na een vastgelopen app', [`${removed} sleutels gewist`, window.location.pathname]);
    window.location.reload();
  };
  render() {
    const {
      error,
      retries,
      confirmReset
    } = this.state;
    if (!error) return this.props.children;
    const chunk = isChunkLoadError(error);
    const stuck = !chunk && retries >= MAX_RETRIES;
    const {
      homeHref = '/',
      homeLabel = 'Naar de homepage',
      offerReset = false,
      fullScreen = false
    } = this.props;
    const atHome = window.location.pathname === homeHref;
    const canReset = offerReset && stuck;
    // Een eigen titel geldt niet voor een chunkfout: daar helpt alleen vernieuwen, en dat moet de titel zeggen.
    const title = chunk ? 'Er staat een nieuwe versie klaar.' : this.props.title ?? (stuck ? 'Dit onderdeel lukt nu niet.' : 'Dit onderdeel laadt even niet.');
    const detail = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    const {
      body = chunk ? 'Vernieuw de pagina om verder te gaan. Je winkelmand blijft bewaard.' : stuck ? canReset ? 'Opnieuw proberen helpt hier niet. Lukt het via de knoppen hieronder ook niet, dan zit er waarschijnlijk iets beschadigds in wat deze browser heeft bewaard.' : 'Opnieuw proberen helpt hier niet. Ga verder via de knop hieronder; je winkelmand blijft bewaard.' : 'Probeer het opnieuw, of kies een andere pagina in het menu.'
    } = this.props;
    // Dezelfde knoppen als de rest van de site (ui/styles.ts): ook een foutscherm voelt als Beauty Bewust.
    const primary = btnPrimary;
    const secondary = btnSecondary;
    return <div role="alert" className={fullScreen ? 'flex min-h-screen min-h-dvh w-full flex-col items-center justify-center bg-sand px-6 text-center' : 'mx-auto max-w-xl px-6 py-20 text-center'} data-id="element-9194" data-mp="199o1q48t1stc">
        <AlertCircleIcon className="mx-auto h-6 w-6 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-9195" data-mp="2ouozcsa17rch" />
        <p className="mt-4 font-display text-[24px] text-ink" data-id="element-9196" data-mp="33j33r7n3jnv5">{title}</p>
        <p className="mx-auto mt-2 max-w-sm text-[15px] leading-relaxed text-muted" data-id="element-9197" data-mp="2yq9vwawbzzeu">{body}</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3" data-id="element-9198" data-mp="3chewpgbfqxqr">
          {chunk || stuck ? <button type="button" onClick={() => window.location.reload()} className={stuck && !atHome ? secondary : primary} data-id="element-9199" data-mp="38gbjl1krvft8">
              Pagina vernieuwen
            </button> : <button type="button" onClick={this.retry} className={secondary} data-id="element-9200" data-mp="3hiui59hiz69o">
              Opnieuw proberen
            </button>}
          {/* Gewone link, geen router-Link: werkt ook als de router zelf de fout veroorzaakt. */}
          {stuck && !atHome && <a href={homeHref} className={primary} data-id="element-9201" data-mp="3n6jgtb214plw">
              {homeLabel}
            </a>}
        </div>
        {/* Wat er precies misging, ingeklapt: om door te geven bij een melding. */}
        {!chunk && this.props.showDetails && <details className="mx-auto mt-6 max-w-sm text-left text-[12px] text-muted" data-id="element-9202" data-mp="367kq8d22nq">
            <summary className="cursor-pointer text-center" data-id="element-9203" data-mp="51g7tf8xztg7">Technische details</summary>
            <p className="mt-2 break-words rounded-md bg-white/60 px-3 py-2 font-mono" data-id="element-9204" data-mp="9yq2b9b6uvaw">{detail}</p>
          </details>}
        {canReset && <div className="mx-auto mt-8 max-w-sm border-t border-line pt-6" data-id="element-9205" data-mp="jpawmkjtxdo0">
            <button type="button" onClick={this.reset} className="text-[14px] text-ink underline decoration-ink/30 underline-offset-4 transition-colors duration-150 ease-soft hover:decoration-ink" data-id="element-9206" data-mp="jrf3nbd0yssi">
              {confirmReset ? 'Ja, wis en herlaad' : 'Lokale gegevens herstellen'}
            </button>
            <p className="mt-2 text-[13px] leading-relaxed text-muted" aria-live="polite" data-id="element-9207" data-mp="17tk0h1hgaww3">
              {confirmReset ? 'Je winkelmand en voorkeuren op dit apparaat gaan weg. Je account, bestellingen en afspraken blijven bewaard.' : 'Wist alleen wat deze site in deze browser heeft bewaard.'}
            </p>
          </div>}
      </div>;
  }
}